/**
 * Human-readable, one-line descriptions for every permission slug — the text a
 * role author sees in a tooltip next to each checkbox. The point is HONESTY:
 * the slug names are terse (`conversation:read`) and a few were historically
 * misleading (`run:read` once gated both AgentOps traces AND chat transcripts),
 * so the description says exactly what the slug unlocks today.
 *
 * Typed as `Record<Permission, string>` so it stays exhaustive: add a slug to
 * {@link PERMISSIONS} without describing it here and this file fails to compile.
 */
export const PERMISSION_DESCRIPTIONS = {
    // Workspace + membership
    "workspace:read": "View the Workspace settings page (name, general configuration).",
    "workspace:update": "Change workspace settings (name, general configuration).",
    "member:read": "See the list of workspace members.",
    "member:invite": "Invite new members to the workspace.",
    "member:update-role": "Change a workspace member's role.",
    "member:remove": "Remove a member from the workspace.",
    "invitation:read": "See pending workspace invitations.",
    "invitation:revoke": "Cancel a pending workspace invitation.",
    "api-key:read": "See the workspace's API keys.",
    "api-key:create": "Create a new API key.",
    "api-key:revoke": "Revoke an API key.",
    "signing-key:manage": "Manage widget signing keys (verified-user authentication).",
    // Agents + knowledge
    "agent:read": "View agents and their configuration.",
    "agent:create": "Create new agents.",
    "agent:update": "Edit agent configuration and publish versions.",
    "agent:delete": "Delete agents.",
    "kb:read": "View knowledge bases and their documents.",
    "kb:upload": "Upload documents to a knowledge base.",
    "kb:update": "Edit knowledge base settings and documents.",
    "kb:delete": "Delete knowledge bases or their documents.",
    "kb:sync": "Trigger a knowledge base sync / re-index.",
    "kb:attach": "Attach a knowledge base to an agent.",
    // Agent runs (AgentOps observability)
    "run:read": "View agent-run traces in AgentOps (LLM calls, tool steps). Not chat transcripts.",
    "run:update": "Rename a conversation and record card submissions.",
    // Human handoff (the desk)
    "handoff:read": "See handoff records and events, and CSAT stats.",
    "handoff:take-over": "Take an AI-handled conversation over yourself (start a handoff).",
    "handoff:reply-all": "Reply to ANY handed-off chat, not only the ones assigned to you.",
    "handoff:claim": "Claim a queued chat off the queue to work it yourself.",
    "handoff:resolve": "Resolve / close a handled chat.",
    "handoff:handback": "Hand a chat back to the AI.",
    "handoff:configure": "Configure the desk: handoff settings, routing teams, agent profiles, CSAT.",
    "handoff:note": "Add an internal note to a chat (never shown to the customer or the AI).",
    "decision:rule": "Rule on the questions agents escalate to the team: accept or deny, with an optional note relayed back to the AI (e.g. approve a refund above the agent's limit).",
    // Conversations (customer chats)
    "conversation:monitor": "Watch the live conversation queue / handoff board.",
    "conversation:read": "Open and read a customer conversation's transcript.",
    // Data tables
    "data-table:read": "View data tables and their rows.",
    "data-table:create": "Create data tables.",
    "data-table:update": "Edit data table schema and rows.",
    "data-table:delete": "Delete data tables.",
    // Feedback
    "feedback:read": "Review the thumbs-up / down feedback customers leave on replies.",
    "feedback:create": "Leave technical feedback on a response.",
    "feedback:manage": "Manage the feedback reason catalogue.",
    // Organization (account tier)
    "org:read": "View the organization (account) this workspace belongs to.",
    "org:manage": "Manage organization settings (SSO, org-wide configuration).",
    "org-member:read": "See the organization's members.",
    "org-member:invite": "Invite members to the organization.",
    "org-member:update-role": "Change an organization member's role.",
    "org-member:remove": "Remove a member from the organization.",
    "billing:read": "View billing and usage.",
    "billing:manage": "Manage billing (plan, payment method, invoices).",
    "workspace:create": "Create new workspaces in the organization.",
    "platform:staff": "CometChat platform staff: cross-tenant support access to every workspace.",
    // Workflows
    "workflow:read": "View workflows and their runs.",
    "workflow:create": "Create new workflows.",
    "workflow:update": "Edit workflow definitions.",
    "workflow:delete": "Delete workflows.",
    "workflow:publish": "Publish a workflow version (make it the live one).",
    "workflow:run": "Start a workflow run.",
    "workflow:signal": "Respond to a workflow that is waiting (approvals, inputs).",
    "workflow:cancel": "Cancel an in-progress workflow run.",
    "workflow:run-as": "Run a workflow on behalf of a specific contact (impersonation).",
    // Stored queries
    "query:read": "View stored queries.",
    "query:create": "Create stored queries.",
    "query:update": "Edit stored queries.",
    "query:delete": "Delete stored queries.",
    "query:run": "Run a stored query.",
    // Helpdesk team delegation
    "helpdesk:manage-members": "Add, remove, and edit the helpdesk team roster.",
    "helpdesk:manage-roles": "Create and edit custom helpdesk roles.",
    "helpdesk:read-members": "See the helpdesk team roster.",
};
/**
 * Look up a slug's description. Accepts an arbitrary string (role editors get
 * their slug lists from the API as `string[]`) and returns `undefined` for one
 * that isn't in the catalogue, so callers can fall back to the raw slug.
 */
export function describePermission(slug) {
    return PERMISSION_DESCRIPTIONS[slug];
}
/**
 * Display-label overrides for slugs whose action verb reads wrong when naively
 * title-cased — `decision:rule` becomes "Rule", which scans as a noun ("a
 * rule") instead of the verb (to rule on / adjudicate). "Approve / deny" is
 * also too narrow: a ruling is accept/deny PLUS an optional note back to the
 * AI. Editors fall back to the generated label when a slug has no entry here.
 */
const PERMISSION_ACTION_LABELS = {
    "decision:rule": "Rule on requests",
};
export function permissionActionLabel(slug) {
    return PERMISSION_ACTION_LABELS[slug];
}
//# sourceMappingURL=permission-descriptions.js.map